import { useRef } from 'react'
import styles from './ActivityImageCarousel.module.css'

interface ActivityImageCarouselProps {
  images: string[]
  activeIndex: number
  onIndexChange: (index: number) => void
}

export default function ActivityImageCarousel({
  images,
  activeIndex,
  onIndexChange,
}: ActivityImageCarouselProps) {
  const touchStartX = useRef<number | null>(null)
  const touchEndX = useRef<number | null>(null)
  const minSwipeDistance = 50

  if (images.length === 0) return null

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.targetTouches[0].clientX
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX
  }

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return

    const distance = touchStartX.current - touchEndX.current
    const isLeftSwipe = distance > minSwipeDistance
    const isRightSwipe = distance < -minSwipeDistance

    if (isLeftSwipe && activeIndex < images.length - 1) {
      onIndexChange(activeIndex + 1)
    } else if (isRightSwipe && activeIndex > 0) {
      onIndexChange(activeIndex - 1)
    }

    touchStartX.current = null
    touchEndX.current = null
  }

  return (
    <div className={styles.carousel}>
      <div
        className={styles.viewport}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className={styles.track}
          style={{
            width: `${images.length * 100}%`,
            transform: `translateX(-${(activeIndex * 100) / images.length}%)`,
          }}
        >
          {images.map((src, i) => (
            <div
              key={i}
              className={styles.slide}
              style={{ width: `${100 / images.length}%` }}
            >
              <span className={styles.stepBadge}>{i + 1}</span>
              <img src={src} alt={`Step ${i + 1}`} />
            </div>
          ))}
        </div>
      </div>
      {images.length > 1 && (
        <div className={styles.dots}>
          {images.map((_, i) => (
            <button
              key={i}
              className={`${styles.dot} ${i === activeIndex ? styles.active : ''}`}
              onClick={() => onIndexChange(i)}
              aria-label={`Go to image ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  )
}
